/** Domain types. Field names match the database columns exactly, so rows from
 *  Supabase need no mapping — see migrations/V1 and V3. */

export type SpeakerType = "youth" | "adult";
export type MemberStatus = "active" | "moved_out";
export type AssignmentRole = "speaker" | "opening_prayer" | "closing_prayer";
export type MeetingType =
  | "regular"
  | "fast_sunday"
  | "stake_conference"
  | "ward_conference"
  | "general_conference";

/** A row of sacrament_member_summary. Everything derived on read — there are no
 *  stored counters to drift. Future-dated meetings are excluded by the view. */
export interface MemberSummary {
  id: string;
  first_name: string;
  last_name: string;
  /** "Last, First" — built by the view so households sort together. */
  sort_name: string;
  speaker_type: SpeakerType;
  status: MemberStatus;
  created_at: string;
  updated_at: string;
  last_spoke_date: string | null;
  speak_count: number;
  last_prayer_date: string | null;
  prayer_count: number;
  is_considering: boolean;
}

/** Exactly one of member_id / guest_name is set — enforced by a check
 *  constraint. Guests are visitors from outside the ward; they exist so the
 *  meeting record is complete and never appear in Up Next. */
export interface Assignment {
  id: string;
  meeting_id: string;
  member_id: string | null;
  guest_name: string | null;
  role: AssignmentRole;
  topic: string | null;
  position: number;
  created_at: string;
}

export interface Meeting {
  id: string;
  meeting_date: string;
  meeting_type: MeetingType;
  notes: string | null;
  created_at: string;
  updated_at: string;
  assignments: Assignment[];
}

/** One talk or prayer, derived client-side from cached meetings. */
export interface HistoryEntry {
  date: string;
  role: AssignmentRole;
  topic: string | null;
}

/** A speaker or prayer slot being filled in on the Record screen, before it
 *  becomes an Assignment. */
export interface Slot {
  memberId?: string;
  guest?: string;
  topic?: string;
}

export interface MeetingTypeConfig {
  v: MeetingType;
  label: string;
  speakers: boolean;
  prayers: boolean;
}

/** Which fields the Record screen shows per type.
 *
 *  Fast Sunday keeps prayers because ward members still offer them. Ward
 *  conference keeps both because ward members often speak alongside visitors.
 *  The two conference types collapse to just a date — the record exists purely
 *  to say the Sunday is accounted for, which is what makes a genuinely missed
 *  week detectable.
 *
 *  An "other" type was proposed and explicitly rejected: escape-hatch values
 *  absorb things that should be their own type.
 */
export const MEETING_TYPES: MeetingTypeConfig[] = [
  { v: "regular", label: "Regular", speakers: true, prayers: true },
  { v: "fast_sunday", label: "Fast Sunday", speakers: false, prayers: true },
  { v: "ward_conference", label: "Ward conference", speakers: true, prayers: true },
  { v: "stake_conference", label: "Stake conference", speakers: false, prayers: false },
  { v: "general_conference", label: "General conference", speakers: false, prayers: false },
];

const FALLBACK: MeetingTypeConfig = MEETING_TYPES[0];

/** Never returns undefined — callers read `.speakers` / `.label` directly. An
 *  unknown value can only mean the enum gained a member without this list being
 *  updated, in which case behaving like a regular meeting is the safe default. */
export function typeOf(v: MeetingType): MeetingTypeConfig {
  return MEETING_TYPES.find((t) => t.v === v) ?? FALLBACK;
}

export const ROLE_LABEL: Record<AssignmentRole, string> = {
  speaker: "Spoke",
  opening_prayer: "Opening prayer",
  closing_prayer: "Closing prayer",
};
